import { useInfiniteQuery } from '@tanstack/react-query';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { Stakeholder, StakeholderOrder, StakeholderType } from '../../types/stakeholder.types';

export const useInfiniteStakeholders = ({
  companyId,
  numberOfFetchedItems = 50,
  enabled = true,
  search,
  sortOrder = StakeholderOrder.ASC,
  type,
}: {
  companyId: string;
  enabled?: boolean;
  numberOfFetchedItems?: number;
  search: string;
  sortOrder?: StakeholderOrder;
  type?: StakeholderType;
}) => {
  const {
    data: stakeholdersData,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useInfiniteQuery<{
    stakeholders: Stakeholder[];
    totalPages: number;
  }>({
    queryKey: [
      QueryKey.GET_STAKEHOLDERS,
      companyId,
      {
        search,
        sortOrder,
        type,
      },
    ],
    queryFn: ({ pageParam = 0 }) => {
      return ApiService.get(
        { endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'stakeholders-list'] },
        {
          queryParams: {
            take: numberOfFetchedItems,
            skip: pageParam,
            search,
            sortOrder,
            ...(type && { type }),
          },
        },
      );
    },
    enabled: !!companyId && enabled,
    initialPageParam: 0,
    refetchOnReconnect: true,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.totalPages > allPages.length ? allPages.length * numberOfFetchedItems : undefined,
  });
  const stakeholders =
    stakeholdersData?.pages?.reduce<Stakeholder[]>((acc, page) => {
      return [...acc, ...page.stakeholders];
    }, []) || [];

  return {
    stakeholders: stakeholders || [],
    hasNextPage,
    fetchNextPage,
    stakeholdersData,
    isEmpty: stakeholders?.length === 0,
    numberOfFetchedItems,
    isLoading,
  };
};
